import { GridApiPro, GridDimensions } from '@chronosphereio/chrono-ui';
import { useCallback, useEffect, useState } from 'react';

interface UseTableLegendScrollbarProps {
  // Making this optional to put some extra type guards in place because there can be some weirdness
  // with the first time the hook gets run before the datagrid has been rendered.
  apiRef?: React.MutableRefObject<GridApiPro>;
}

interface UseTableLegendScrollbarResponse {
  /**
   * Indicates if the table legend has a horizontal scrollbar.
   */
  hasScrollX?: GridDimensions['hasScrollX'];
}

/**
 * Hook that helps us keep track of scrollbar-related information about the datagrid used for the
 * table legend. This is helpful for certain responsive rendering logic.
 */
export const useTableLegendScrollbar = ({ apiRef }: UseTableLegendScrollbarProps): UseTableLegendScrollbarResponse => {
  // Do not bother with trying to initialize this. The first time this hook gets called the apiRef is unlikely
  // to be updated because the datagrid has not rendered yet.
  const [hasScrollX, setHasScrollX] = useState<GridDimensions['hasScrollX']>();

  const handleViewportInnerSizeChange = useCallback(() => {
    const dimensions = apiRef?.current.getRootDimensions();
    if (dimensions) {
      // Grab the state of the horizontal scrollbar when available.
      setHasScrollX(dimensions.hasScrollX);
    }
  }, [apiRef]);

  useEffect(() => {
    // Listen for viewport change events on the datagrid. Return the result to ensure event listener
    // is cleaned up.
    return apiRef?.current.subscribeEvent('viewportInnerSizeChange', handleViewportInnerSizeChange);
  }, [apiRef, handleViewportInnerSizeChange]);

  return {
    hasScrollX,
  };
};
